import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { Loader2, Search, Inbox, FolderOpen, CheckCircle2, AlertTriangle, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusBadge, CategoryBadge, PriorityBadge, AITriageBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { STATUSES, CATEGORIES, CAMPUSES } from "@/lib/constants";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export default function SpecialistDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [concerns, setConcerns] = useState(null);
  const [filters, setFilters] = useState({ status: "all", category: "all", campus: "all", search: "" });

  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, [concerns]);

  const loadConcerns = useCallback(() => {
    const params = {};
    if (filters.status !== "all") params.status = filters.status;
    if (filters.category !== "all") params.category = filters.category;
    if (filters.campus !== "all") params.campus = filters.campus;
    if (filters.search.trim()) params.search = filters.search.trim();
    api.get("/concerns", { params }).then((r) => setConcerns(r.data)).catch(() => setConcerns([]));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(loadConcerns, 250);
    return () => clearTimeout(t);
  }, [loadConcerns]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const cards = stats
    ? [
        { label: "Total concerns", value: stats.total, icon: Inbox, tone: "text-forest-600 bg-forest-50" },
        { label: "Open", value: stats.open, icon: FolderOpen, tone: "text-terracotta-500 bg-terracotta-50" },
        { label: "Urgent", value: stats.by_priority?.urgent || 0, icon: AlertTriangle, tone: "text-destructive bg-destructive/10" },
        { label: "Resolved", value: stats.resolved, icon: CheckCircle2, tone: "text-[#4A7c59] bg-[#4A7c59]/12" },
      ]
    : [];

  const chartData = (stats?.by_category || []).filter((c) => c.count > 0);
  const COLORS = ["#2C4A3B", "#C27E67", "#8FB09B", "#D4AF37", "#5C7B89", "#A86B56", "#3F6551"];

  return (
    <Layout showFooter={false}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div>
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta-500">Specialist workspace</span>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-forest-900">Case management</h1>
          <p className="mt-2 text-charcoal-600">Welcome, {user?.name}. Triage, assign, and resolve concerns across every campus.</p>
        </div>

        {/* Stats */}
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-forest-600/10 p-5 shadow-[0_4px_24px_rgba(44,74,59,0.04)]" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className={`grid place-items-center h-10 w-10 rounded-xl ${c.tone}`}><c.icon className="h-5 w-5" strokeWidth={1.5} /></span>
              <p className="mt-4 font-serif text-3xl text-forest-900">{c.value}</p>
              <p className="text-sm text-charcoal-600">{c.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          {/* Chart */}
          <div className="bg-white rounded-2xl border border-forest-600/10 p-6 shadow-sm">
            <h3 className="font-serif text-lg text-forest-900">Concerns by category</h3>
            {chartData.length === 0 ? (
              <p className="mt-8 text-sm text-charcoal-600">No data yet.</p>
            ) : (
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#4A5340" }} interval={0} angle={-18} textAnchor="end" height={50} />
                    <Tooltip cursor={{ fill: "rgba(44,74,59,0.05)" }} contentStyle={{ borderRadius: 12, border: "1px solid #DCE7DF", fontSize: 12 }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Status breakdown */}
          <div className="bg-white rounded-2xl border border-forest-600/10 p-6 shadow-sm lg:col-span-2">
            <h3 className="font-serif text-lg text-forest-900">Pipeline</h3>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
              {STATUSES.map((s) => (
                <button key={s.value} onClick={() => setF("status", s.value)} className="text-left rounded-xl border border-forest-600/10 p-4 hover:bg-forest-50/50 transition-colors" data-testid={`pipeline-${s.value}`}>
                  <p className="font-serif text-2xl text-forest-900">{stats?.by_status?.[s.value] ?? 0}</p>
                  <p className="text-xs text-charcoal-600 mt-0.5">{s.label}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-600" strokeWidth={1.5} />
            <Input value={filters.search} onChange={(e) => setF("search", e.target.value)} data-testid="case-search-input"
              placeholder="Search by title or case number…" className="pl-9 bg-white rounded-full border-forest-600/15 h-11" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger className="w-[150px] bg-white rounded-full border-forest-600/15 h-11" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v) => setF("category", v)}>
            <SelectTrigger className="w-[180px] bg-white rounded-full border-forest-600/15 h-11" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.campus} onValueChange={(v) => setF("campus", v)}>
            <SelectTrigger className="w-[170px] bg-white rounded-full border-forest-600/15 h-11" data-testid="filter-campus"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campuses</SelectItem>
              {CAMPUSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Case list */}
        <div className="mt-6">
          {concerns === null ? (
            <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest-600" /></div>
          ) : concerns.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-forest-600/10">
              <p className="text-charcoal-600">No concerns match these filters.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {concerns.map((c, i) => (
                <motion.button
                  key={c.id}
                  onClick={() => navigate(`/admin/cases/${c.id}`)}
                  data-testid={`case-row-${c.case_number}`}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}
                  className="group text-left bg-white rounded-2xl border border-forest-600/10 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-charcoal-600">{c.case_number}</span>
                        <StatusBadge status={c.status} />
                        <PriorityBadge priority={c.priority} />
                        {c.is_anonymous && <span className="text-[11px] rounded-full bg-bone-100 px-2 py-0.5 text-charcoal-600">Anonymous</span>}
                        {c.ai_triage?.generated && <AITriageBadge />}
                      </div>
                      <h3 className="mt-2 font-serif text-xl text-forest-900 truncate">{c.title}</h3>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-charcoal-600">
                        <CategoryBadge category={c.category} />
                        {c.campus && <span>• {c.campus}</span>}
                        <span>• {fmt(c.created_at)}</span>
                        {c.assigned_to_name ? <span>• {c.assigned_to_name}</span> : <span className="text-terracotta-500">• Unassigned</span>}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-charcoal-600 group-hover:text-terracotta-500 group-hover:translate-x-1 transition-all shrink-0" strokeWidth={1.5} />
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
